import * as XLSX from 'xlsx';
import {ForecastSchedule, ScheduleItem} from '../models/types';
import {getCellDateValue, getCellValue} from '@/features/email-generation';

/**
 * Service for parsing forecasting Excel files and extracting schedule information
 */
export class ForecastingParserService {
  /**
   * Parses an Excel file and extracts forecasting schedule information
   * @param file The Excel file as File object or ArrayBuffer
   * @returns A ForecastSchedule object with the extracted data
   */
  public async parseExcelFile(file: File | ArrayBuffer): Promise<ForecastSchedule> {
    // Read the Excel file
    const data = file instanceof File 
      ? await this.readFileAsArrayBuffer(file) 
      : file;
    
    const workbook = XLSX.read(data, { type: 'array' });
    
    // Nur nach dem INTERNAL SCHEDULE sheet suchen
    const scheduleSheet = this.findSheetByName(workbook, 'INTERNAL SCHEDULE');
    
    if (!scheduleSheet) {
      throw new Error('Could not find INTERNAL SCHEDULE sheet in the Excel file');
    }
    
    // Extract schedule items from INTERNAL SCHEDULE sheet
    const items = this.extractScheduleItemsFromInternalSchedule(scheduleSheet);
    
    // Determine period from the items (e.g., "Q1 2025")
    const period = this.determinePeriod(items);
    
    // Create and return the ForecastSchedule object
    return {
      items,
      period
    };
  }
  
  /**
   * Reads a File as ArrayBuffer
   * @param file The file to read
   * @returns Promise resolving to ArrayBuffer
   */
  private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as ArrayBuffer);
      reader.onerror = (e) => reject(e);
      reader.readAsArrayBuffer(file);
    });
  }
  
  /**
   * Finds a sheet by name
   * @param workbook The workbook
   * @param name The sheet name to find
   * @returns The worksheet or undefined if not found
   */
  private findSheetByName(workbook: XLSX.WorkBook, name: string): XLSX.WorkSheet | undefined {
    const sheetName = workbook.SheetNames.find((n: string) => n.includes(name));
    return sheetName ? workbook.Sheets[sheetName] : undefined;
  }
  
  /**
   * Extracts schedule items from the SUMMARY sheet
   * @param sheet The SUMMARY sheet
   * @returns Array of ScheduleItem objects
   */
  private extractScheduleItemsFromSummary(sheet: XLSX.WorkSheet): ScheduleItem[] {
    const items: ScheduleItem[] = [];
    const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1:A1');
    
    // Skip the header row (row 0)
    for (let row = 1; row <= range.e.r; row++) {
      const titleCell = XLSX.utils.encode_cell({ r: row, c: 0 }); // Column A
      // Column B enthält Kommentare zum Start/Ende, die wir in den Titel integrieren können
      const startDateCell = XLSX.utils.encode_cell({ r: row, c: 2 }); // Column C
      const endDateCell = XLSX.utils.encode_cell({ r: row, c: 3 }); // Column D
      const otherCommentsCell = XLSX.utils.encode_cell({ r: row, c: 4 }); // Column E
      
      const title = getCellValue(sheet, titleCell);
      const startDate = getCellDateValue(sheet, startDateCell);
      
      if (title && startDate instanceof Date) {
        const item: ScheduleItem = {
          title: title.toString(),
          startDate,
          type: this.determineItemType(title.toString()),
          comments: getCellValue(sheet, otherCommentsCell)?.toString()
        };
        
        const endDate = getCellDateValue(sheet, endDateCell);
        if (endDate instanceof Date) {
          item.endDate = endDate;
        }
        
        items.push(item);
      }
    }
    
    return items;
  }
  
  /**
   * Extracts schedule items from the INTERNAL SCHEDULE sheet
   * @param sheet The INTERNAL SCHEDULE sheet
   * @returns Array of ScheduleItem objects
   */
  private extractScheduleItemsFromInternalSchedule(sheet: XLSX.WorkSheet): ScheduleItem[] {
    const items: ScheduleItem[] = [];
    const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1:A1');
    
    // Beginnen bei Zeile 6 (Index 5) und bis zur letzten Zeile im Arbeitsblatt gehen
    for (let row = 5; row <= range.e.r; row++) {
      // Prüfen, ob die Zelle in Spalte B einen Inhalt hat
      const activityCell = XLSX.utils.encode_cell({ r: row, c: 1 }); // Spalte B
      const activity = getCellValue(sheet, activityCell);
      
      // Wenn keine Aktivität in dieser Zeile, überspringen und zur nächsten gehen
      if (!activity) {
        continue;
      }
      
      // Involved Parties aus Spalte C auslesen
      const involvedPartyCell = XLSX.utils.encode_cell({ r: row, c: 2 }); // Spalte C
      const involvedParty = getCellValue(sheet, involvedPartyCell);
      
      // In den Spalten E bis Z nach Datumswerten suchen (Indizes 4 bis 25)
      for (let col = 4; col <= Math.min(25, range.e.c); col++) {
        const dateCell = XLSX.utils.encode_cell({ r: row, c: col });
        const dateValue = getCellDateValue(sheet, dateCell);
        
        if (dateValue instanceof Date) {
          // Wir haben ein Datum gefunden, also erstellen wir einen Eintrag
          const item: ScheduleItem = {
            title: activity.toString(),
            startDate: dateValue,
            type: this.determineItemType(activity.toString()),
            comments: involvedParty ? involvedParty.toString() : undefined
          };
          
          items.push(item);
        }
      }
    }
    
    return items;
  }
  
  /**
   * Determines the type of a schedule item based on its title
   * @param title The title of the schedule item
   * @returns The type of the schedule item
   */
  private determineItemType(title: string): 'meeting' | 'process' | 'other' {
    const lowerTitle = title.toLowerCase();
    
    if (lowerTitle.includes('meeting')) {
      return 'meeting';
    } else if (lowerTitle.includes('fc') || lowerTitle.includes('ewp') || lowerTitle.includes('bep')) {
      return 'process';
    } else {
      return 'other';
    }
  }
  
  /**
   * Determines the period of the forecast schedule based on the items
   * @param items The schedule items
   * @returns The period of the forecast schedule
   */
  private determinePeriod(items: ScheduleItem[]): string {
    // Try to find a title that contains a quarter reference (e.g., "FC Q1 2025")
    for (const item of items) {
      const match = item.title.match(/FC\s*Q(\d)\s*(\d{4})/i);
      if (match) {
        return `Q${match[1]} ${match[2]}`;
      }
    }
    
    // If no quarter reference is found, use the year from the first item's start date
    if (items.length > 0) {
      const year = items[0].startDate.getFullYear();
      return `${year}`;
    }
    
    return 'Unknown Period';
  }
}
